import type { Listing } from "@/services/api";
import { listingsService } from "@/services/api";
import { getPriceHistoryCache } from "@/utils/priceHistoryCache";
import { useCallback, useEffect, useRef, useState } from "react";

interface ChartPriceHistoryData {
    date: string;
    avgPrice?: number;
    soldAvgPrice?: number;
    count?: number;
    price?: number;
    isInitialListing?: boolean;
    isMarketAtListing?: boolean;
    isInterpolated?: boolean;
    isSoldImportantPoint?: boolean;
}

interface ChartHistoricalPriceData {
    date: string;
    price?: number;
    avgPrice?: number;
    soldAvgPrice?: number;
    isInitialListing?: boolean;
    isMarketAtListing?: boolean;
    count?: number;
}

interface UsePriceHistoryResult {
    priceHistoryData: ChartPriceHistoryData[];
    historicalPriceData: ChartHistoricalPriceData[];
    similarListingsCount: number;
    soldListingsCount: number;
    isLoading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
}

interface UsePriceHistoryOptions {
    includeCurrentPrice?: boolean;
    listing?: Listing;
}

const transformApiDataToChartFormat = (
    apiPriceHistory: Record<string, unknown>[],
    apiHistoricalData: Record<string, unknown>[],
    listing?: Listing
): { priceHistory: ChartPriceHistoryData[], historicalData: ChartHistoricalPriceData[] } => {

    const chartPriceHistory: ChartPriceHistoryData[] = apiPriceHistory.map(item => ({
        date: item.date as string,
        avgPrice: (item.avgPrice || item.avg_price) as number,
        soldAvgPrice: (item.soldAvgPrice || item.sold_avg_price) as number,
        count: (item.count as number) || 0,
        price: item.price as number,
        isInitialListing: item.isInitialListing as boolean || false,
        isMarketAtListing: item.isMarketAtListing as boolean || false,
        isInterpolated: item.isInterpolated as boolean || false,
        isSoldImportantPoint: item.isSoldImportantPoint as boolean || false,
    }));

    const chartHistoricalData: ChartHistoricalPriceData[] = apiHistoricalData.map(item => ({
        date: item.date as string,
        price: item.price as number,
        avgPrice: (item.avgPrice || item.avg_price) as number,
        soldAvgPrice: (item.soldAvgPrice || item.sold_avg_price) as number,
        isInitialListing: item.isInitialListing as boolean || false,
        isMarketAtListing: item.isMarketAtListing as boolean || false,
        count: (item.count as number) || 0,
    }));

    if (listing?.current_price) {
        const today = new Date().toISOString().split('T')[0];
        const existingTodayIndex = chartPriceHistory.findIndex(item => item.date === today);

        const currentPricePoint: ChartPriceHistoryData = {
            date: today,
            avgPrice: listing.market_status?.avg_price,
            soldAvgPrice: listing.market_status?.sold_avg_price,
            count: 1,
            price: listing.current_price,
            isInitialListing: false,
            isMarketAtListing: false,
            isInterpolated: false,
            isSoldImportantPoint: false,
        };

        if (existingTodayIndex >= 0) {
            chartPriceHistory[existingTodayIndex] = {
                ...chartPriceHistory[existingTodayIndex],
                price: listing.current_price,
            };
        } else {
            chartPriceHistory.push(currentPricePoint);
        }
    }

    chartPriceHistory.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    chartHistoricalData.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    return {
        priceHistory: chartPriceHistory,
        historicalData: chartHistoricalData
    };
};

export const usePriceHistory = (
    listingId: string | null,
    options: UsePriceHistoryOptions = {}
): UsePriceHistoryResult => {
    const { includeCurrentPrice = true, listing } = options;



    const [priceHistoryData, setPriceHistoryData] = useState<ChartPriceHistoryData[]>([]);
    const [historicalPriceData, setHistoricalPriceData] = useState<ChartHistoricalPriceData[]>([]);
    const [similarListingsCount, setSimilarListingsCount] = useState(0);
    const [soldListingsCount, setSoldListingsCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);

    const fetchPriceHistory = useCallback(async () => {
        if (!listingId) {
            setPriceHistoryData([]);
            setHistoricalPriceData([]);
            setSimilarListingsCount(0);
            setSoldListingsCount(0);
            return;
        }

        const cache = getPriceHistoryCache();
        const cacheKey = `${listingId}-${includeCurrentPrice ? 'with-current' : 'no-current'}`;
        const cached = cache.get(cacheKey);

        if (cached) {
            const { priceHistory, historicalData, similarCount, soldCount } = cached as {
                priceHistory: ChartPriceHistoryData[];
                historicalData: ChartHistoricalPriceData[];
                similarCount: number;
                soldCount: number;
            };
            setPriceHistoryData(priceHistory);
            setHistoricalPriceData(historicalData);
            setSimilarListingsCount(similarCount);
            setSoldListingsCount(soldCount);
            return;
        }

        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        try {
            setIsLoading(true);
            setError(null);

            console.log(`[usePriceHistory] Fetching price history for listing: ${listingId}`);
            const response = await listingsService.getPriceHistory(listingId);

            if (abortController.signal.aborted) {
                return;
            }

            if (response) {
                const {
                    price_history,
                    historical_price_data,
                    similar_listings_count,
                    sold_listings_count,
                } = response;

                const transformed = transformApiDataToChartFormat(
                    (price_history || []) as unknown as Record<string, unknown>[],
                    (historical_price_data || []) as unknown as Record<string, unknown>[],
                    includeCurrentPrice ? listing : undefined
                );

                cache.set(cacheKey, {
                    priceHistory: transformed.priceHistory,
                    historicalData: transformed.historicalData,
                    similarCount: similar_listings_count || 0,
                    soldCount: sold_listings_count || 0,
                });

                setPriceHistoryData(transformed.priceHistory);
                setHistoricalPriceData(transformed.historicalData);
                setSimilarListingsCount(similar_listings_count || 0);
                setSoldListingsCount(sold_listings_count || 0);
            }
        } catch (err) {
            if (abortController.signal.aborted) {
                return;
            }

            console.error("Error fetching price history:", err);
            setError(err instanceof Error ? err.message : "Failed to fetch price history");

            setPriceHistoryData([]);
            setHistoricalPriceData([]);
            setSimilarListingsCount(0);
            setSoldListingsCount(0);
        } finally {
            if (!abortController.signal.aborted) {
                setIsLoading(false);
            }
        }
    }, [listingId, includeCurrentPrice, listing]);

    useEffect(() => {
        fetchPriceHistory();

        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [fetchPriceHistory]);

    return {
        priceHistoryData,
        historicalPriceData,
        similarListingsCount,
        soldListingsCount,
        isLoading,
        error,
        refetch: fetchPriceHistory,
    };
};